import type { TFunction } from "i18next";

import { ApiError } from "@/api/errors";
import type { components } from "@/api/schema";

export type ActivationReason = components["schemas"]["ActivationReason"];

/** One human line per reason: "Rule N: <reason>" or "<reason>" for promotion-level codes. */
export function formatActivationReasons(reasons: ActivationReason[], t: TFunction): string[] {
  return reasons.map((r) => {
    const text = t(`precheck.reason.${r.code}`);
    return r.rule_sequence != null
      ? `${t("precheck.rule_prefix", { n: r.rule_sequence })}: ${text}`
      : text;
  });
}

/** RHF field path that a precheck reason points at, or null when it has no field. */
export function reasonFieldPath(reason: ActivationReason): string | null {
  const idx = reason.rule_sequence != null ? reason.rule_sequence - 1 : null;
  switch (reason.code) {
    case "quota_zero":
      return idx == null ? null : `rules.${idx}.gift_limit_quantity`;
    case "missing_add_on":
      return idx == null ? null : `rules.${idx}.products`;
    case "missing_trigger_or_min_spend":
      return idx == null ? null : `rules.${idx}.is_min_spend`;
    case "invalid_dates":
      return "end_at";
    case "no_rules":
      return "rules";
    default:
      return null;
  }
}

/** `reasons[]` from a 422 `activation_invalid` transition error; null for any other error. */
export function activationReasonsFromError(err: unknown): ActivationReason[] | null {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== "object") return null;
  const b = err.body as { error?: unknown; reasons?: unknown };
  if (b.error !== "activation_invalid" || !Array.isArray(b.reasons)) return null;
  return b.reasons as ActivationReason[];
}
